import { onBeforeUnmount, shallowRef, type Ref } from 'vue'

/**
 * 天地图 POI（兴趣点）搜索结果选项
 */
export interface TMapPoiOption {
  /** 显示文本（用于 AutoComplete 组件） */
  text: string
  /** 选项值 */
  value: string
  /** POI 名称 */
  name?: string
  /** POI 地址 */
  address?: string
  /** 经纬度数组 [经度, 纬度] */
  lnglat: number[]
  /** 其他扩展字段 */
  [key: string]: any
}

/**
 * 本地搜索配置选项
 */
interface UseTMapLocalSearchOptions {
  /** 每页显示的搜索结果数量，默认为20 */
  pageCapacity?: number
}

/**
 * 天地图本地搜索 Hook
 * 提供地点搜索功能，支持搜索 POI（兴趣点）
 *
 * @param Map - 地图实例的 ref 引用
 * @param options - 搜索配置选项
 * @returns 搜索相关的方法和状态
 */
export function useTMapLocalSearch(Map: Ref<any>, options?: UseTMapLocalSearchOptions) {
  const T = window.T

  /** 本地搜索实例 */
  const localSearch = shallowRef<any>(null)

  /** POI 搜索结果选项列表 */
  const poiOptions = shallowRef<TMapPoiOption[]>([])

  const { pageCapacity = 20 } = options ?? {}

  /**
   * 初始化本地搜索功能
   * 创建搜索实例并配置搜索完成回调
   */
  const initLocalSearch = () => {
    if (!Map.value) return

    localSearch.value = new T.LocalSearch(Map.value, {
      pageCapacity,
      onSearchComplete(res: any) {
        const pois = res.getPois() || []
        // 将搜索结果转换为组件可用的选项格式
        poiOptions.value = pois.map((item: any) => ({
          ...item,
          text: item.hotPointID,
          value: `${item.name}-${item.address}`,
          key: item.hotPointID,
          lnglat: item.lonlat.split(',').map((v: string) => Number(v))
        }))
      }
    })
  }

  /**
   * 执行地点搜索
   *
   * @param word - 搜索关键词
   */
  const search = (word: string) => {
    if (!localSearch.value) return
    // 清空之前的搜索结果
    poiOptions.value = []
    // 执行搜索，参数：关键词，页码
    localSearch.value.search(word, 1)
  }

  /**
   * 销毁本地搜索实例
   * 清空搜索引用，释放资源
   */
  const destroyLocalSearch = () => {
    // 与原实现对齐：仅置空引用
    localSearch.value = null
  }

  // 组件卸载时自动销毁搜索实例
  onBeforeUnmount(destroyLocalSearch)

  return {
    /** POI 搜索结果选项列表 */
    options: poiOptions,
    /** 初始化搜索功能方法 */
    initLocalSearch,
    /** 执行搜索方法 */
    search,
    /** 销毁搜索实例方法 */
    destroyLocalSearch
  }
}
